import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { places, getPlace } from './places';
import MapCanvas from './MapCanvas';
import './explorer.css';

export default function BelvoirExplorer({
  assetBase = '',
  initialPlace = 'castle',
  embedded = false,
}) {
  const start = getPlace(initialPlace) || places[0];
  const [selected, setSelected] = useState(start);
  const [category, setCategory] = useState(start.category);
  const [focusRequest, setFocusRequest] = useState(0);
  const [hunt, setHunt] = useState(false);
  const [found, setFound] = useState([]);
  const [status, setStatus] = useState('Opening the gardens');
  const [announcement, setAnnouncement] = useState('');
  const huntId = useId();

  const select = useCallback((id) => {
    const p = getPlace(id);
    if (!p) return;
    setSelected(p);
    setCategory(p.category);
    setFocusRequest((n) => n + 1);
    setAnnouncement(`${p.name} selected.`);
  }, []);

  const find = useCallback((id) => {
    if (!getPlace(id)?.bee) return;
    setFound((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setAnnouncement(`You found the bee at ${getPlace(id).name}.`);
  }, []);

  const selectRef = useRef(select);
  selectRef.current = select;

  useEffect(() => {
    const readHash = () => {
      const id = new URLSearchParams(window.location.hash.slice(1)).get('place');
      if (id && getPlace(id)) selectRef.current(id);
    };
    readHash();
    window.addEventListener('hashchange', readHash);
    return () => window.removeEventListener('hashchange', readHash);
  }, []);

  return (
    <main
      className={`belvoir-explorer${embedded ? ' be-site-embedded' : ''}`}
      aria-label="Belvoir garden explorer"
    >
      <a className="be-skip" href="#belvoir-places">
        Skip to the place list
      </a>

      {!embedded && (
        <header className="be-header">
          <div className="be-brand">
            <div>
              <strong>BELVOIR</strong>
              <span>CASTLE & GARDENS</span>
            </div>
          </div>
          <span className="be-header-title">The garden explorer</span>
          <a href="/your-visit/opening-times">Plan your visit →</a>
        </header>
      )}

      <div className="be-workspace">
        <aside className="be-sidebar" id="belvoir-places">
          <p className="be-eyebrow">A landscape to discover</p>
          <h1>Explore the gardens</h1>
          <p className="be-intro">Choose a place. Take a closer look.</p>

          <div className="be-tabs" role="tablist" aria-label="Types of places">
            <button
              type="button"
              className={`be-tab${category === 'gardens' ? ' is-active' : ''}`}
              onClick={() => setCategory('gardens')}
            >
              Gardens
            </button>
            <button
              type="button"
              className={`be-tab${category === 'essentials' ? ' is-active' : ''}`}
              onClick={() => setCategory('essentials')}
            >
              Visitor essentials
            </button>
          </div>

          <nav
            aria-label={category === 'gardens' ? 'Gardens and heritage' : 'Visitor facilities'}
            className="be-places"
          >
            {places
              .filter((p) => p.category === category)
              .map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`be-place${selected.id === p.id ? ' is-active' : ''}`}
                  aria-pressed={selected.id === p.id}
                  onClick={() => select(p.id)}
                >
                  <span>{p.number}</span>
                  <span>{p.name}</span>
                  <span>{found.includes(p.id) && hunt ? '✓' : '›'}</span>
                </button>
              ))}
          </nav>

          <section className="be-detail" aria-live="polite" aria-atomic="true">
            <p className="be-eyebrow">{selected.kind}</p>
            <h2>{selected.name}</h2>
            <p>{selected.description}</p>
            <p>{selected.detail}</p>
            <a className="be-source" href={selected.source} target="_blank" rel="noreferrer">
              About this place ↗
            </a>
            {hunt && selected.bee && (
              <div className="be-discovery">
                <div>
                  <strong>
                    {found.includes(selected.id)
                      ? 'A little discovery'
                      : 'There’s a bee to find here'}
                  </strong>
                  {found.includes(selected.id) ? (
                    <p>{selected.bee}</p>
                  ) : (
                    <button type="button" onClick={() => find(selected.id)}>
                      Find this garden bee
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>

          <div className="be-hunt">
            <label htmlFor={huntId}>
              <strong>Garden bee hunt</strong>
              <span>
                {hunt
                  ? `${found.length} of 5 discovered`
                  : 'A little adventure for curious minds'}
              </span>
            </label>
            <input
              id={huntId}
              type="checkbox"
              checked={hunt}
              onChange={(e) => {
                setHunt(e.target.checked);
                if (e.target.checked) setCategory('gardens');
              }}
            />
          </div>
          {hunt && (
            <div className="be-hunt-detail" aria-live="polite">
              {found.length === 5
                ? 'You found all five! A whole garden of discoveries.'
                : 'Find the golden bee markers on this digital map, or use the place list.'}
              {found.length > 0 && (
                <button
                  type="button"
                  style={{
                    marginLeft: 8,
                    background: 'none',
                    border: 0,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: 'inherit',
                    fontFamily: 'inherit',
                  }}
                  onClick={() => {
                    setFound([]);
                    setAnnouncement('Bee hunt restarted.');
                  }}
                >
                  Start again
                </button>
              )}
            </div>
          )}
        </aside>

        <section className="be-map" aria-label="Interactive 3D map">
          <MapCanvas
            assetBase={assetBase.replace(/\/$/, '')}
            selected={selected.id}
            focusRequest={focusRequest}
            category={category}
            hunt={hunt}
            found={found}
            onSelect={select}
            onFind={find}
            onStatus={setStatus}
          />
        </section>
      </div>

      <footer className="be-footer">
        <span className="be-status">
          <span aria-hidden="true" />
          Illustrated landscape · from your Blender model · explore, don’t navigate
        </span>
        <div>
          <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
            © OpenStreetMap contributors · ODbL
          </a>
        </div>
      </footer>

      <div className="sr-only" role="status" aria-live="polite">
        {status} {announcement}
      </div>
    </main>
  );
}
